/**
 * ÉÉN bron voor het reviewplatform (Kiyoh). Staat in `packages/core` zodat de site (badges,
 * /reviews, "schrijf een review" in het account) en de Cloud Functions (het automatische
 * reviewverzoek per mail, zie functions/src/orders/sendReviewRequests.ts) exact dezelfde link
 * gebruiken. Zonder deze gedeelde bron stuurde de mail klanten naar onze eigen reviewpagina
 * terwijl de site ze naar Kiyoh stuurde — twee plekken die uit de pas kunnen lopen.
 *
 * Gekoppeld op 09-08-2026 met de gegevens uit het Kiyoh/Klantenvertellen-dashboard van Glastotaal.
 *
 * `tenantId` hoort bij het widget-adres en is NIET optioneel: zonder die parameter geeft
 * retrieve-widget.html een lege pagina terug.
 *
 * LET OP — Kiyoh levert géén insluit-script. Een eerdere opzet laadde
 * `kiyoh.com/v1/review/external/review.js?locationId=…`; dat adres bestaat niet (geverifieerd
 * 09-08-2026: HTTP 404). Alleen het iframe hieronder werkt.
 *
 * Het cijfer zelf zit in het iframe of achter een afgeschermd statistieken-eindpunt
 * (/v1/publication/review/external/location/statistics — HTTP 401 zonder API-sleutel). Tot die
 * sleutel er is tonen we nergens een los cijfer: een waardering claimen die we niet kunnen
 * onderbouwen is erger dan geen cijfer.
 */
export const KIYOH = {
  /** Bedrijfs-/vestigings-ID (staat ook in de review- en widget-adressen). */
  locationId: "1081255",
  /** Hoort bij het widget-adres; Kiyoh NL = 98. */
  tenantId: "98",
  /** Uitnodigingspagina: hier laat een klant zijn beoordeling achter. */
  writeReviewUrl: "https://www.kiyoh.com/add-review/1081255/Glastotaal?lang=nl",
  /** Openbaar profiel met alle beoordelingen. */
  profileUrl: "https://www.kiyoh.com/reviews/1081255/Glastotaal?lang=nl",
} as const;

/**
 * Adres van het insluitbare Kiyoh-widget (iframe).
 *
 * `thema`: "licht" op een witte pagina, "donker" op onze navy vlakken. Dat scheelt een wit kader:
 * Kiyoh's eigen stylesheet zet bij `color=dark` de tekst op wit én laat de achtergrond doorzichtig
 * (`.kv-widget-body.theme-dark{background:#464343;color:#fff}` + `.transparent{background:0 0}`,
 * geverifieerd 10-08-2026). Alleen "dark" schakelt dat om — "black", "grey" en de rest vallen stil
 * terug op het lichte thema, dus niet zomaar een andere waarde invullen.
 */
export const kiyohWidgetSrc = (thema: "licht" | "donker" = "licht"): string =>
  `https://www.kiyoh.com/retrieve-widget.html?color=${thema === "donker" ? "dark" : "white"}` +
  `&allowTransparency=true&button=true&lang=nl` +
  `&tenantId=${KIYOH.tenantId}&locationId=${KIYOH.locationId}`;

/**
 * Kiyoh's Invite API: wij geven de bestelling door, Kiyoh stuurt de uitnodiging (en 4 dagen later
 * hun eigen herinnering). Vervangt onze eigen reviewmail.
 *
 * Het adres staat NERGENS in hun dashboard — daar staat alleen de sleutel met een lijst
 * parameters. Uitgezocht op 10-08-2026 door adressen af te tasten: alles gaf 404 behalve
 * `/v1/invite/external`, dat met een nette VALIDATION_ERROR antwoordt. Let op de valkuil:
 * `/v1/invite/external/invite` (met dat tweede "invite") bestaat níét, terwijl het publicatie-deel
 * van hun API wél zo'n extra segment heeft.
 *
 * `hash` is dezelfde sleutel als voor de statistieken — Kiyoh gebruikt er één per vestiging.
 *
 * Waarschuwing bij testen: een volledige aanroep verstuurt écht een e-mail. Laat je
 * `invite_email` weg, dan krijg je overigens geen validatiefout maar een HTTP 500 — hun kant
 * struikelt daarover.
 */
export const KIYOH_INVITE_URL = "https://www.kiyoh.com/v1/invite/external";

export type KiyohInvite = {
  email: string;
  /** Dagen wachten voor Kiyoh de uitnodiging stuurt (0 = direct, max 50). */
  delayDagen: number;
  voornaam?: string;
  achternaam?: string;
  /** Ordernummer — komt terug in Kiyoh's geschiedenis, zodat je een review kunt terugzoeken. */
  referentie?: string;
  taal?: "nl" | "en";
};

/** Volledige aanroep-URL voor één uitnodiging. */
export function kiyohInviteUrl(sleutel: string, inv: KiyohInvite): string {
  const q = new URLSearchParams({
    hash: sleutel,
    location_id: KIYOH.locationId,
    invite_email: inv.email,
    delay: String(Math.max(0, Math.min(50, Math.round(inv.delayDagen)))),
    language: inv.taal ?? "nl",
  });
  if (inv.voornaam) q.set("first_name", inv.voornaam);
  if (inv.achternaam) q.set("last_name", inv.achternaam);
  if (inv.referentie) q.set("ref_code", inv.referentie);
  return `${KIYOH_INVITE_URL}?${q.toString()}`;
}

/* ─────────────────────────── Productreviews ───────────────────────────
 *
 * Kiyoh's product-API (documentatie van Kiyoh, 10-08-2026). Drie stappen:
 *   1. producten aanmelden        → PUT  /v1/location/product/external[/bulk]
 *   2. klant uitnodigen mét product → POST /v1/invite/external
 *   3. beoordelingen ophalen      → GET  /v1/publication/product[...]
 *
 * `product_code` is ONZE sleutel bij Kiyoh en moet voor altijd bij hetzelfde artikel horen —
 * beoordelingen hangen eraan. Wij gebruiken de **slug**: die is de URL van het artikel, staat al
 * als `g:id` in de Merchant-feed en verandert niet zomaar. Bewust NIET het GT-nummer: dat wordt
 * per categorie doorgenummerd, dus één nieuw artikel in het midden schuift alle nummers daarna op
 * en beoordelingen zouden stilletjes bij een ánder product belanden.
 *
 * Alle aanroepen gebruiken dezelfde sleutel als de statistieken, in de header
 * `X-Publication-Api-Token` — niet als `hash` in de URL (dat is alleen de oudere invite-vorm).
 */

/** Eén product aanmelden of bijwerken (PUT). Zelfde `product_code` = bijwerken. */
export const KIYOH_PRODUCT_URL = "https://www.kiyoh.com/v1/location/product/external";

/** Tot 200 producten tegelijk aanmelden/bijwerken (PUT). */
export const KIYOH_PRODUCT_BULK_URL = `${KIYOH_PRODUCT_URL}/bulk`;

/** Hoeveel producten Kiyoh per bulk-aanroep accepteert. */
export const KIYOH_PRODUCT_BATCH = 200;

export type KiyohProduct = {
  /** Onze slug — de sleutel waar beoordelingen aan hangen. */
  code: string;
  naam: string;
  /** Publieke productfoto (volledige URL). */
  fotoUrl: string;
  /** De pagina waar het product te koop staat. */
  paginaUrl: string;
  /** Mag het beoordeeld worden? `false` haalt het uit de vragenlijsten zonder reviews te wissen. */
  actief: boolean;
  /** Merk, zoals in het Kiyoh-dashboard en (later) in hun doorgifte naar Google. */
  merk?: string;
  /** Ons eigen artikelnummer (GT-…) — hetzelfde nummer dat de klant op de site en de factuur ziet. */
  sku?: string;
};

/** Body-vorm van één product zoals Kiyoh 'm wil (snake_case, hun veldnamen). */
export function kiyohProductBody(p: KiyohProduct): Record<string, string | boolean> {
  const body: Record<string, string | boolean> = {
    location_id: KIYOH.locationId,
    product_code: p.code,
    product_name: p.naam,
    image_url: p.fotoUrl,
    source_url: p.paginaUrl,
    active: p.actief,
  };
  if (p.merk) body.brand_name = p.merk;
  // `skus` (meervoud) is Kiyoh's veldnaam, ook voor één waarde. GTIN en MPN laten we leeg: een
  // GTIN hebben we niet, en het MPN-veld zou het Timmers-artikelnummer publiek maken — dat is
  // inkoopinformatie en hoort niet op een openbare reviewpagina.
  if (p.sku) body.skus = p.sku;
  return body;
}

/** Body voor de bulk-aanroep: Kiyoh wil het location_id zowel buiten als binnen elk product. */
export function kiyohProductBulkBody(producten: KiyohProduct[]): Record<string, unknown> {
  return { location_id: KIYOH.locationId, products: producten.map(kiyohProductBody) };
}

/** Alle bij Kiyoh bekende producten opvragen (GET) — om te zien wat er al staat. */
export const kiyohProductListUrl = (): string =>
  `${KIYOH_PRODUCT_URL}?locationId=${KIYOH.locationId}`;

/**
 * Producten mét nieuwe beoordelingen sinds een moment (GET). Eén aanroep voor de hele catalogus,
 * in plaats van per product vragen — bij 2.400 artikelen is dat het verschil tussen één verzoek
 * per nacht en 2.400. `sinds` is UTC ISO, bv. 2026-08-01T00:00:00.000Z.
 */
export const kiyohProductUpdatesUrl = (sinds: string): string =>
  `https://www.kiyoh.com/v1/publication/product` +
  `?locationId=${KIYOH.locationId}&updatedSince=${encodeURIComponent(sinds)}`;

/** De beoordelingen van één product (GET) — voor een gerichte controle of herstel. */
export const kiyohProductReviewsUrl = (productCode: string): string =>
  `https://www.kiyoh.com/v1/publication/product/review/external` +
  `?locationId=${KIYOH.locationId}&productCode=${encodeURIComponent(productCode)}`;

/**
 * Uitnodiging als JSON-body (POST). Dit is de vorm die óók productbeoordelingen kan vragen; de
 * oudere `kiyohInviteUrl` (alles in de URL) kan dat niet.
 *
 * `product_invite` laten we bewust op false/weg: dan vraagt Kiyoh één winkelbeoordeling én de
 * productbeoordelingen in dezelfde vragenlijst. Op true zou de klant alléén het product
 * beoordelen en droogt het winkelcijfer op.
 *
 * Maximaal drie producten per uitnodiging — Kiyoh's eigen advies: bij meer wordt de vragenlijst
 * zo lang dat mensen afhaken, en dan levert een grote bestelling juist mínder reviews op.
 */
export const KIYOH_MAX_PRODUCTS_PER_INVITE = 3;

export function kiyohInviteBody(inv: KiyohInvite & { productCodes?: string[] }): Record<string, unknown> {
  const body: Record<string, unknown> = {
    location_id: KIYOH.locationId,
    invite_email: inv.email,
    delay: Math.max(0, Math.min(50, Math.round(inv.delayDagen))),
    language: inv.taal ?? "nl",
  };
  if (inv.voornaam) body.first_name = inv.voornaam;
  if (inv.achternaam) body.last_name = inv.achternaam;
  if (inv.referentie) body.ref_code = inv.referentie;
  const codes = (inv.productCodes ?? []).filter(Boolean).slice(0, KIYOH_MAX_PRODUCTS_PER_INVITE);
  if (codes.length) body.product_code = codes;
  return body;
}

/** Kiyoh-widget beschikbaar (bedrijfs-ID ingevuld). */
export const kiyohHasWidget = (): boolean => KIYOH.locationId.trim().length > 0;

/** "Review achterlaten" gaat naar Kiyoh (uitnodigingslink ingevuld). */
export const kiyohCanWrite = (): boolean => KIYOH.writeReviewUrl.trim().length > 0;
